import React, { useState, useEffect } from 'react';
import { Package, Truck, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ShippingRateSelector = ({ transactionId, onRateSelected, onError }) => {
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/shipping/rates/${transactionId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setRates(response.data.data);
        if (response.data.data.length > 0) {
          // Pre-select cheapest option
          setSelectedRate(response.data.data[0]);
          onRateSelected(response.data.data[0]);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load shipping rates';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRate = (rate) => {
    setSelectedRate(rate);
    onRateSelected(rate);
  };

  const getCarrierLogo = (carrier) => {
    const carriers = {
      USPS: '🇺🇸',
      UPS: '📦',
      FedEx: '✈️',
    };
    return carriers[carrier] || '📮';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '12px', color: 'var(--textSecondary)' }}>
          Loading shipping rates...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', background: 'var(--error)', color: 'white', borderRadius: '8px' }}>
        <p>{error}</p>
        <button
          onClick={fetchRates}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            background: 'white',
            color: 'var(--error)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (rates.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--textSecondary)' }}>
        <Package size={48} style={{ margin: '0 auto 12px' }} />
        <p>No shipping options available</p>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Truck size={20} />
        Select Shipping Method
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {rates.map((rate) => (
          <div
            key={rate.objectId}
            onClick={() => handleSelectRate(rate)}
            style={{
              padding: '16px',
              border: `2px solid ${selectedRate?.objectId === rate.objectId ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: selectedRate?.objectId === rate.objectId ? 'var(--primaryLight)' : 'white',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '20px' }}>{getCarrierLogo(rate.provider)}</span>
                  <strong>{rate.provider}</strong>
                  <span style={{ color: 'var(--textSecondary)' }}>-</span>
                  <span>{rate.servicelevel}</span>
                </div>
                {rate.estimatedDays && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: 'var(--textSecondary)' }}>
                    <Clock size={14} />
                    <span>{rate.estimatedDays} {rate.estimatedDays === 1 ? 'day' : 'days'}</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)' }}>
                ${rate.amount.toFixed(2)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedRate && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'var(--backgroundSecondary)',
          borderRadius: '6px',
          fontSize: '14px',
        }}>
          <strong>Selected:</strong> {selectedRate.provider} {selectedRate.servicelevel} - ${selectedRate.amount.toFixed(2)}
        </div>
      )}
    </div>
  );
};

export default ShippingRateSelector;
